import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterUserMutation } from '../../services/userAuthApi';
import { storeToken } from '../../storage/LocalStorageService';
import "react-datepicker/dist/react-datepicker.css";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const Registration = () => {
  const [server_error, setServerError] = useState({});
  const navigate = useNavigate();
  const [gender, setGender] = useState('');
  const [registerUser, { isLoading }] = useRegisterUserMutation();
  const {t} = useTranslation("auth");
  const translation = t('registration_tran');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const actualData = {
      email: data.get('email'),
      full_name: data.get('fullname'),
      gender: gender,
      password: data.get('password'),
      password2: data.get('password2'),
    };

    const res = await registerUser(actualData);

    if (res.error) {
      // Handle both field errors and non-field errors
      setServerError({
        ...res.error.data?.errors,         // Field-specific errors
        non_field_errors: res.error.data?.errors?.non_field_errors || []
      });
    }
    if (res.data) {
      storeToken(res.data.token);
      navigate('/auth/verify-email', { state: { email: actualData.email } });
    }
  };

  return (
    <>
      <Helmet>
        <title>Register - AddaZakat</title>
        <meta name="description" content="Create an account on AddaZakat to start giving or receiving Zakat and donations in a transparent and impactful way." />
        <link rel="canonical" href="https://addazakat.com/auth/register/" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <form className="" onSubmit={handleSubmit}>
        {/* Full Name Input */}
        <div className="mb-4">
          <label htmlFor="fullname" className="block font-bold text-black dark:text-stone-50 mb-2">{translation.full_name_label}</label>
          <input 
            type="text" 
            id="fullname" 
            name="fullname" 
            required 
            placeholder={translation.full_name_placeholder}
            className="az-input-btn" />
          {server_error.full_name && <p className="az-error-message">{server_error.full_name[0]}</p>}
        </div>

        {/* Gender Selection */}
        <div className="mb-6"> 
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {translation.gender_label}
          </label>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setGender('female')}
              className={`
                w-full py-2 px-4 rounded-md text-sm font-medium
                border transition duration-200 ease-in-out
                ${gender === 'female'
                  ? 'bg-blue-600 text-white shadow-md border-blue-600 hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              `}
            >
              {translation.female}
            </button>
            <button
              type="button"
              onClick={() => setGender('male')}
              className={`
                w-full py-2 px-4 rounded-md text-sm font-medium
                border transition duration-200 ease-in-out
                ${gender === 'male'
                  ? 'bg-blue-600 text-white shadow-md border-blue-600 hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              `}
            >
              {translation.male}
            </button>
          </div>
          {server_error.gender && (
            <p className="az-error-message">
              {server_error.gender[0]}
            </p>
          )}
        </div>
        {/* Email Input */}
        <div className="mb-4">
          <label htmlFor="email" className="block font-bold text-black dark:text-stone-50 mb-2">{translation.email_label}</label>
          <input 
            type="email" 
            id="email" 
            name="email" 
            required 
            placeholder={translation.email_placeholder}
            className="az-input-btn" />
          {server_error.email && <p className="az-error-message">{server_error.email[0]}</p>}
        </div>

        {/* Password Inputs */}
        <div className="mb-4 relative">
          <label htmlFor="password" className="block font-bold text-black dark:text-stone-50 mb-2">{translation.password_label}</label>
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            required
            className="az-input-btn"
            placeholder={translation.password_placeholder}
          />
          <span
            className="absolute right-3 text-2xl top-[41px] cursor-pointer text-gray-500"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
          </span>
          {server_error.password && <p className="az-error-message">{server_error.password[0]}</p>}
        </div>


        <div className="mb-4 relative">
          <label htmlFor="password2" className="block font-bold text-black dark:text-stone-50 mb-2">{translation.confirm_password_label}</label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="password2"
            name="password2"
            required
            className="az-input-btn"
            placeholder={translation.confirm_password_placeholder}
          />
          <span
            className="absolute right-3 text-2xl top-[41px] cursor-pointer text-gray-500"
            onClick={() => setShowConfirmPassword((prev) => !prev)}
          >
            {showConfirmPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
          </span>
          {server_error.password2 && <p className="az-error-message">{server_error.password2[0]}</p>}
        </div>


        {/* Submit Button */}
        <div className="text-center">
          {isLoading ? (
            <div className="inline-block loading-icon">
              <AiOutlineLoading3Quarters className="animate-spin" />
            </div>
          ) : (
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-4"
            >
              {translation.signup_button}
            </button>
          )}
        </div>

        {/* Non-field Errors */}
        {server_error.non_field_errors && (
          <div className="text-red-500 text-center text-base mt-2 font-bold text-black dark:text-white ">
            {server_error.non_field_errors.map((error, index) => (
              <p key={index} className='az-error-message'>{error}</p>
            ))}
          </div>
        )}
      </form>
    </>

  );
};

export default Registration;